import * as React from 'react';

import type { Handle } from '@shared/utils/animation-timeout';
import {
  cancelAnimationTimeout,
  requestAnimationTimeout,
} from '@shared/utils/animation-timeout';

export const useDebouncedHandler = <T extends (...args: any[]) => any>(
  time: number,
  handler: T,
  { flushOnUnmount = true }: { flushOnUnmount?: boolean } = {},
): T => {
  const timeoutHandle = React.useRef<Handle | null>(null);
  const pendingArgs = React.useRef<Parameters<T> | null>(null);
  const handlerRef = React.useRef(handler);

  React.useLayoutEffect(() => {
    handlerRef.current = handler;
  });

  React.useEffect(() => {
    return () => {
      cancelAnimationTimeout(timeoutHandle.current);
      const args = pendingArgs.current;
      pendingArgs.current = null;
      if (flushOnUnmount && args != null) {
        handlerRef.current(...args);
      }
    };
  }, [flushOnUnmount]);

  const [runDebouncedHandler] = React.useState(
    () =>
      (...args: Parameters<T>) => {
        cancelAnimationTimeout(timeoutHandle.current);
        pendingArgs.current = args;
        timeoutHandle.current = requestAnimationTimeout(time, () => {
          pendingArgs.current = null;
          handlerRef.current(...args);
        });
      },
  );

  return runDebouncedHandler as T;
};

export const useDebouncedValue = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  const timeoutHandle = React.useRef<Handle | null>(null);

  React.useEffect(() => {
    cancelAnimationTimeout(timeoutHandle.current);
    timeoutHandle.current = requestAnimationTimeout(delay, () => {
      setDebouncedValue(value);
    });

    return () => {
      cancelAnimationTimeout(timeoutHandle.current);
    };
  }, [value, delay]);

  return debouncedValue;
};
